import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeaders = () => {
    const t = localStorage.getItem("fliboe_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me", { headers: authHeaders() });
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip /me check — AuthCallback handles it.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const storeSession = (data) => {
    if (data?.session_token) localStorage.setItem("fliboe_token", data.session_token);
    setUser(data.user);
  };

  const loginEmail = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    storeSession(data);
    return data;
  };

  const registerEmail = async (email, password, name, ref, extras = {}) => {
    const { data } = await api.post("/auth/register", {
      email, password, name, ref,
      age_bracket: extras.age_bracket || undefined,
      gender: extras.gender || undefined,
      country: extras.country || undefined,
    });
    storeSession(data);
    return data;
  };

  const forgotPassword = async (email) => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const { data } = await api.post("/auth/forgot-password", { email, origin });
    return data;
  };

  const resetPassword = async (token, password) => {
    const { data } = await api.post("/auth/reset-password", { token, password });
    storeSession(data);
    return data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout", {}, { headers: authHeaders() }); } catch (_) {}
    localStorage.removeItem("fliboe_token");
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, loginEmail, registerEmail, forgotPassword, resetPassword, loginWithGoogle, logout, checkAuth, authHeaders }}>
      {children}
    </AuthCtx.Provider>
  );
}

// Helper for components that don't use the context (e.g. modules outside Auth tree)
export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
